interface DrawerDragOptions {
  minHeight?: number
  maxHeight?: number
}

// Minimum height (in dvh) before the drawer auto-closes on release
const DRAG_VISUAL_FLOOR_DVH = 10

export function useDrawerDrag(handleRef: Ref<HTMLElement | undefined | null>, open: Ref<boolean>, options: DrawerDragOptions = {}) {
  const { minHeight = 90, maxHeight = 100 } = options

  const height = ref<number>(minHeight)
  const isDragging = ref<boolean>(false)

  const { height: windowHeight } = useWindowSize()

  let startY = 0
  let startHeightValue = minHeight

  function convertPxToVh(px: number) {
    return windowHeight.value ? (px / windowHeight.value) * 100 : 0
  }

  function onPointerDown(e: PointerEvent) {
    isDragging.value = true
    startY = e.clientY
    startHeightValue = height.value
    ; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: PointerEvent) {
    if (!isDragging.value) {
      return
    }
    const deltaVh = convertPxToVh(e.clientY - startY)
    height.value = Math.min(Math.max(startHeightValue - deltaVh, DRAG_VISUAL_FLOOR_DVH), maxHeight)
  }

  function onPointerUp() {
    if (!isDragging.value) {
      return
    }
    isDragging.value = false

    // If the drawer is released below the visible threshold, close it
    // to avoid content becoming inaccessible below the viewport
    if (height.value < minHeight) {
      open.value = false
    }
  }

  watch(handleRef, (el, _, onCleanup) => {
    if (!el) {
      return
    }

    if (!import.meta.client) {
      return
    }

    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    el.style.touchAction = 'none'

    onCleanup(() => {
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    })
  }, { immediate: true })

  // Reset height when drawer closes
  watch(open, (isOpen) => {
    if (!isOpen) {
      height.value = minHeight
      isDragging.value = false
    }
  })

  const heightStyle = computed(() => `${height.value}dvh`)

  return {
    height,
    heightStyle,
    isDragging,
  }
}
